import monitoringStatusTemplate from "@/data/templates/pages/monitoring-status-dashboard.json";
import monitoringStatusTemplateV2 from "@/data/templates/pages/monitoring-status-dashboard-v2.json";
import { Component, ComponentType } from "@/types";

type TemplateComponentShape = {
  id: string;
  type: string;
  name?: string;
  parentId?: string;
  pageId?: string;
  order?: number;
  locked?: boolean;
  hidden?: boolean;
  properties?: Record<string, unknown>;
  position?: Component["position"];
  size?: Component["size"];
  style?: Record<string, unknown>;
  responsivePosition?: Component["responsivePosition"];
  children?: string[];
};

type LegacyCardMigration = {
  legacyId: string;
  nextId: string;
  labelId: string;
  valueId: string;
  subtitleId: string;
  iconId: string;
};

const COMPONENT_TYPE_VALUES = new Set<string>(Object.values(ComponentType));

function isTemplateComponentShape(value: unknown): value is TemplateComponentShape {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  return typeof value.id === "string" && typeof value.type === "string";
}

function getTemplateReferenceComponents(
  components: unknown[] | undefined,
): TemplateComponentShape[] {
  if (!Array.isArray(components)) {
    return [];
  }

  return components.filter(isTemplateComponentShape);
}

function isComponentType(value: string): value is ComponentType {
  return COMPONENT_TYPE_VALUES.has(value);
}

function getComponentType(value: string): ComponentType {
  if (isComponentType(value)) {
    return value;
  }

  throw new Error(`Unsupported template component type: ${value}`);
}

const MONITORING_REFERENCE_COMPONENTS = getTemplateReferenceComponents(
  monitoringStatusTemplate.pages?.[0]?.components,
);

const MONITORING_REFERENCE_BY_ID = new Map(
  MONITORING_REFERENCE_COMPONENTS.map((component) => [component.id, component]),
);

const MONITORING_V2_REFERENCE_COMPONENTS = getTemplateReferenceComponents(
  monitoringStatusTemplateV2.pages?.[0]?.components,
);

const MONITORING_V2_REFERENCE_BY_ID = new Map(
  MONITORING_V2_REFERENCE_COMPONENTS.map((component) => [component.id, component]),
);

const LEGACY_BREADCRUMB_IDS = new Set([
  "breadcrumb-wrapper",
  "breadcrumb-row",
  "home-icon",
  "chevron-separator-1",
  "breadcrumb-project",
  "chevron-separator-2",
  "current-page",
]);

const LEGACY_MONITORING_CARD_MIGRATIONS: LegacyCardMigration[] = [
  {
    legacyId: "data-card-pressure-out",
    nextId: "pressure-out-card",
    labelId: "pressure-out-label",
    valueId: "pressure-out-value",
    subtitleId: "pressure-out-subtitle",
    iconId: "pressure-out-icon",
  },
  {
    legacyId: "data-card-well-status",
    nextId: "well-status-card",
    labelId: "well-status-label",
    valueId: "well-status-value",
    subtitleId: "well-status-subtitle",
    iconId: "well-status-icon",
  },
  {
    legacyId: "data-card-well-pressure",
    nextId: "well-pressure-card",
    labelId: "well-pressure-label",
    valueId: "well-pressure-value",
    subtitleId: "well-pressure-subtitle",
    iconId: "well-pressure-icon",
  },
  {
    legacyId: "data-card-flow-rate",
    nextId: "flow-rate-card",
    labelId: "flow-rate-label",
    valueId: "flow-rate-value",
    subtitleId: "flow-rate-subtitle",
    iconId: "flow-rate-icon",
  },
];

const MONITORING_LAYOUT_TYPES = new Set<string>([
  ComponentType.BREADCRUMB,
  ComponentType.FLEX_CONTAINER,
  ComponentType.TEXT,
  ComponentType.ICON,
  ComponentType.BUTTON,
]);

const TEXT_PROPERTY_KEYS_TO_PRESERVE = [
  "content",
  "queryTemplateId",
  "dataSource",
  "expression",
  "format",
];

const BUTTON_PROPERTY_KEYS_TO_PRESERVE = [
  "text",
  "label",
  "variant",
  "linkToPageId",
  "actionType",
];

const ICON_PROPERTY_KEYS_TO_PRESERVE = [
  "iconName",
  "library",
  "color",
  "label",
  "cursor",
  "linkToPageId",
];

const WELL_LOCATION_SUMMARY_COMPACT_GAUGE_IDS = new Set([
  "pressure-gauge-chart",
  "flow-gauge-chart",
]);

const WELL_LOCATION_SUMMARY_COMPACT_SENSOR_CARD_IDS = new Set([
  "well-pressure-sensor-card",
  "well-flow-sensor-card",
]);

const WELL_LOCATION_SUMMARY_TEXT_MARKERS = [
  "well location summary",
  "well water sensors",
];

const WELL_LOCATION_SUMMARY_GAUGE_LABELS = new Set(["PSI", "GPM"]);

const WELL_LOCATION_SUMMARY_COMPACT_GAUGE_STYLE = {
  height: 110,
  width: "100%",
  minHeight: 90,
};

const WELL_LOCATION_SUMMARY_COMPACT_SENSOR_CARD_BASE_STYLE = {
  height: "auto",
  gap: 8,
  padding: "16px",
  justifyContent: "flex-start",
};

const WELL_LOCATION_SUMMARY_COMPACT_SENSOR_CARD_MOBILE_STYLE = {
  gap: 8,
  padding: "16px",
};

function cloneValue<T>(value: T): T {
  if (value === undefined || value === null) {
    return value;
  }

  return structuredClone(value);
}

function cloneComponent(component: Component): Component {
  return {
    ...component,
    properties: cloneValue(component.properties || {}),
    position: component.position ? cloneValue(component.position) : component.position,
    size: cloneValue(component.size),
    style: cloneValue(component.style || {}),
    responsivePosition: cloneValue(component.responsivePosition),
    children: [...(component.children || [])],
    _childComponents: undefined,
    interactions: component.interactions
      ? cloneValue(component.interactions)
      : component.interactions,
    metadata: component.metadata ? cloneValue(component.metadata) : component.metadata,
  };
}

function applyChildrenReplacement(
  component: Component,
  previousId: string,
  nextId: string,
): Component {
  if (!component.children.includes(previousId)) {
    return component;
  }

  return {
    ...component,
    children: component.children.map((childId) =>
      childId === previousId ? nextId : childId,
    ),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isResponsiveStyle(style: unknown): style is Record<string, unknown> {
  return (
    isRecord(style) &&
    ("base" in style || "mobile" in style || "tablet" in style || "desktop" in style)
  );
}

function normalizeResponsiveStyle(
  style: Record<string, unknown> | undefined,
  basePatch: Record<string, unknown>,
  mobilePatch?: Record<string, unknown>,
): Record<string, unknown> {
  if (!style || !isRecord(style)) {
    return {
      base: basePatch,
      mobile: mobilePatch ?? {},
      tablet: {},
      desktop: {},
    };
  }

  if (isResponsiveStyle(style)) {
    const baseStyle = isRecord(style.base) ? style.base : {};
    const mobileStyle = isRecord(style.mobile) ? style.mobile : {};

    return {
      ...style,
      base: {
        ...baseStyle,
        ...basePatch,
      },
      ...(mobilePatch
        ? {
            mobile: {
              ...mobileStyle,
              ...mobilePatch,
            },
          }
        : {}),
    };
  }

  return {
    ...style,
    ...basePatch,
  };
}

function isWellLocationSummaryGauge(component: Component): boolean {
  return (
    component.type === ComponentType.GAUGE_CHART ||
    (component.type === ComponentType.CHART &&
      component.properties?.chartType === "gauge-chart")
  );
}

function isWellLocationSummarySemanticGauge(component: Component): boolean {
  return (
    isWellLocationSummaryGauge(component) &&
    typeof component.properties?.label === "string" &&
    WELL_LOCATION_SUMMARY_GAUGE_LABELS.has(component.properties.label)
  );
}

function getWellLocationSummaryTextMarker(component: Component): string {
  const markerValues = [
    component.properties?.content,
    component.properties?.text,
    component.properties?.label,
    component.properties?.title,
    component.name,
  ];

  return markerValues
    .filter(
      (value): value is string | number =>
        typeof value === "string" || typeof value === "number",
    )
    .map((value) => String(value).trim().toLowerCase())
    .join(" ");
}

function hasWellLocationSummaryContext(components: Component[]): boolean {
  return components.some((component) => {
    const marker = getWellLocationSummaryTextMarker(component);
    return WELL_LOCATION_SUMMARY_TEXT_MARKERS.some((text) => marker.includes(text));
  });
}

function repairWellLocationSummaryGaugeCards(components: Component[]): Component[] {
  const hasWellLocationContext = hasWellLocationSummaryContext(components);
  const compactSensorCardIds = new Set(WELL_LOCATION_SUMMARY_COMPACT_SENSOR_CARD_IDS);

  if (hasWellLocationContext) {
    for (const component of components) {
      if (
        isWellLocationSummarySemanticGauge(component) &&
        typeof component.parentId === "string"
      ) {
        compactSensorCardIds.add(component.parentId);
      }
    }
  }

  return components.map((component) => {
    const shouldNormalizeGauge =
      WELL_LOCATION_SUMMARY_COMPACT_GAUGE_IDS.has(component.id) ||
      (hasWellLocationContext && isWellLocationSummarySemanticGauge(component));
    const shouldNormalizeSensorCard =
      component.type === ComponentType.FLEX_CONTAINER && compactSensorCardIds.has(component.id);

    if (!shouldNormalizeGauge && !shouldNormalizeSensorCard) {
      return component;
    }

    if (shouldNormalizeSensorCard) {
      return {
        ...component,
        size: {
          ...component.size,
          height: "auto",
        },
        style: normalizeResponsiveStyle(
          component.style,
          WELL_LOCATION_SUMMARY_COMPACT_SENSOR_CARD_BASE_STYLE,
          WELL_LOCATION_SUMMARY_COMPACT_SENSOR_CARD_MOBILE_STYLE,
        ),
      };
    }

    return {
      ...component,
      size: {
        ...component.size,
        height: 110,
      },
      properties: {
        ...component.properties,
        valueFontSize: 16,
        labelFontSize: 9,
      },
      style: normalizeResponsiveStyle(
        component.style,
        WELL_LOCATION_SUMMARY_COMPACT_GAUGE_STYLE,
      ),
    };
  });
}

function materializeReferenceComponent(
  referenceComponent: TemplateComponentShape,
  context: Component,
): Component {
  const timestamp = context.updatedAt ?? context.createdAt ?? new Date();

  return {
    id: referenceComponent.id,
    projectId: context.projectId,
    pageId: context.pageId,
    name: referenceComponent.name || context.name,
    type: getComponentType(referenceComponent.type),
    properties: cloneValue(referenceComponent.properties || {}),
    position: referenceComponent.position
      ? cloneValue(referenceComponent.position)
      : context.position,
    size: referenceComponent.size
      ? cloneValue(referenceComponent.size)
      : cloneValue(context.size),
    style: cloneValue(referenceComponent.style || {}),
    responsivePosition: referenceComponent.responsivePosition
      ? cloneValue(referenceComponent.responsivePosition)
      : cloneValue(context.responsivePosition),
    parentId: referenceComponent.parentId,
    children: [...(referenceComponent.children || [])],
    order: referenceComponent.order ?? context.order,
    zIndex: context.zIndex,
    locked: context.locked ?? referenceComponent.locked ?? false,
    hidden: context.hidden ?? referenceComponent.hidden ?? false,
    version: context.version ?? 1,
    createdBy: context.createdBy,
    lastModifiedBy: context.lastModifiedBy,
    lastModifiedAt: context.lastModifiedAt ?? timestamp,
    isDeleted: false,
    deletedAt: context.deletedAt,
    createdAt: context.createdAt ?? timestamp,
    updatedAt: context.updatedAt ?? timestamp,
    interactions: context.interactions
      ? cloneValue(context.interactions)
      : undefined,
    metadata: context.metadata ? cloneValue(context.metadata) : undefined,
  };
}

function getReferenceSubtree(rootId: string): TemplateComponentShape[] {
  const visited = new Set<string>();
  const queue = [rootId];
  const subtree: TemplateComponentShape[] = [];

  while (queue.length > 0) {
    const currentId = queue.shift();
    if (!currentId || visited.has(currentId)) {
      continue;
    }

    visited.add(currentId);
    const component = MONITORING_REFERENCE_BY_ID.get(currentId);
    if (!component) {
      continue;
    }

    subtree.push(component);
    for (const childId of component.children || []) {
      queue.push(childId);
    }
  }

  return subtree;
}

function formatLegacyCardLabel(label: unknown): string | null {
  if (typeof label !== "string" || label.trim().length === 0) {
    return null;
  }

  return label.trim().toUpperCase();
}

function formatLegacyCardValue(value: unknown, unit: unknown): string | null {
  if (value === undefined || value === null) {
    return null;
  }

  const normalizedUnit =
    typeof unit === "string" && unit.trim().length > 0 ? unit.trim() : null;

  if (typeof value === "number") {
    return normalizedUnit ? `${value} ${normalizedUnit}` : `${value}`;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (!normalizedUnit || trimmed.toUpperCase().includes(normalizedUnit.toUpperCase())) {
      return trimmed;
    }

    return `${trimmed} ${normalizedUnit}`;
  }

  return normalizedUnit ? `${String(value)} ${normalizedUnit}` : String(value);
}

function pickDefinedProperties(
  properties: Record<string, unknown>,
  keys: string[],
): Record<string, unknown> {
  const picked: Record<string, unknown> = {};

  for (const key of keys) {
    if (properties[key] !== undefined) {
      picked[key] = properties[key];
    }
  }

  return picked;
}

function mergePropertiesWithReference(
  componentType: string,
  currentProperties: Record<string, unknown>,
  referenceProperties: Record<string, unknown>,
): Record<string, unknown> {
  if (componentType === ComponentType.TEXT || componentType === ComponentType.DATA_TEXT) {
    return {
      ...referenceProperties,
      ...pickDefinedProperties(currentProperties, TEXT_PROPERTY_KEYS_TO_PRESERVE),
    };
  }

  if (componentType === ComponentType.BUTTON) {
    return {
      ...referenceProperties,
      ...pickDefinedProperties(currentProperties, BUTTON_PROPERTY_KEYS_TO_PRESERVE),
    };
  }

  if (componentType === ComponentType.ICON) {
    return {
      ...referenceProperties,
      ...pickDefinedProperties(currentProperties, ICON_PROPERTY_KEYS_TO_PRESERVE),
    };
  }

  return referenceProperties;
}

function rebaseMonitoringComponentToReference(component: Component): Component {
  const referenceComponent = MONITORING_REFERENCE_BY_ID.get(component.id);
  if (!referenceComponent || !MONITORING_LAYOUT_TYPES.has(referenceComponent.type)) {
    return component;
  }

  const referenced = materializeReferenceComponent(referenceComponent, component);
  return {
    ...component,
    name: referenced.name,
    type: referenced.type,
    properties: mergePropertiesWithReference(
      referenced.type,
      component.properties || {},
      referenced.properties || {},
    ),
    position: referenced.position,
    size: referenced.size,
    style: referenced.style,
    responsivePosition: referenced.responsivePosition,
    parentId: referenced.parentId,
    children: [...referenced.children],
    order: referenced.order,
    createdAt: component.createdAt,
    updatedAt: component.updatedAt,
    lastModifiedAt: component.lastModifiedAt,
    createdBy: component.createdBy,
    lastModifiedBy: component.lastModifiedBy,
    interactions: component.interactions,
    metadata: component.metadata,
  };
}

function migrateLegacyBreadcrumb(components: Component[]): Component[] {
  const breadcrumbWrapper = components.find((component) => component.id === "breadcrumb-wrapper");
  if (!breadcrumbWrapper || components.some((component) => component.id === "breadcrumb-nav")) {
    return components;
  }

  const breadcrumbReference = MONITORING_REFERENCE_BY_ID.get("breadcrumb-nav");
  if (!breadcrumbReference) {
    return components;
  }

  const migratedBreadcrumb = materializeReferenceComponent(
    breadcrumbReference,
    breadcrumbWrapper,
  );

  return components.flatMap((component) => {
    if (LEGACY_BREADCRUMB_IDS.has(component.id)) {
      if (component.id === "breadcrumb-wrapper") {
        return [migratedBreadcrumb];
      }
      return [];
    }

    return [applyChildrenReplacement(component, "breadcrumb-wrapper", "breadcrumb-nav")];
  });
}

function migrateLegacyMonitoringCard(
  components: Component[],
  migration: LegacyCardMigration,
): Component[] {
  const legacyCard = components.find((component) => component.id === migration.legacyId);
  if (!legacyCard || components.some((component) => component.id === migration.nextId)) {
    return components;
  }

  const subtree = getReferenceSubtree(migration.nextId).map((referenceComponent) =>
    materializeReferenceComponent(referenceComponent, legacyCard),
  );

  const labelContent = formatLegacyCardLabel(legacyCard.properties?.label);
  const valueContent = formatLegacyCardValue(
    legacyCard.properties?.value,
    legacyCard.properties?.unit,
  );
  const subtitleContent =
    typeof legacyCard.properties?.subtitle === "string"
      ? legacyCard.properties.subtitle
      : null;
  const legacyIconName =
    typeof legacyCard.properties?.icon === "string"
      ? legacyCard.properties.icon
      : null;
  const legacyIconColor =
    typeof legacyCard.properties?.iconColor === "string"
      ? legacyCard.properties.iconColor
      : null;

  const patchedSubtree = subtree.map((component) => {
    if (component.id === migration.labelId && labelContent) {
      return {
        ...component,
        properties: {
          ...component.properties,
          content: labelContent,
        },
      };
    }

    if (component.id === migration.valueId && valueContent) {
      return {
        ...component,
        properties: {
          ...component.properties,
          content: valueContent,
        },
      };
    }

    if (component.id === migration.subtitleId && subtitleContent) {
      return {
        ...component,
        properties: {
          ...component.properties,
          content: subtitleContent,
        },
      };
    }

    if (component.id === migration.iconId) {
      return {
        ...component,
        properties: {
          ...component.properties,
          ...(legacyIconName ? { iconName: legacyIconName } : {}),
          ...(legacyIconColor ? { color: legacyIconColor } : {}),
        },
      };
    }

    return component;
  });

  return components.flatMap((component) => {
    if (component.id === migration.legacyId) {
      return patchedSubtree;
    }

    return [applyChildrenReplacement(component, migration.legacyId, migration.nextId)];
  });
}

function isLikelyMonitoringStatusPage(components: Component[]): boolean {
  const componentIds = new Set(components.map((component) => component.id));
  const hasLegacyMonitoringCard = LEGACY_MONITORING_CARD_MIGRATIONS.some((migration) =>
    componentIds.has(migration.legacyId),
  );
  const hasCurrentMonitoringCard = LEGACY_MONITORING_CARD_MIGRATIONS.some((migration) =>
    componentIds.has(migration.nextId),
  );

  return (
    componentIds.has("dashboard-wrapper") &&
    componentIds.has("content-wrapper") &&
    (componentIds.has("metrics-section") ||
      hasLegacyMonitoringCard ||
      hasCurrentMonitoringCard)
  );
}

function rebaseMonitoringStatusLayout(components: Component[]): Component[] {
  if (!isLikelyMonitoringStatusPage(components)) {
    return components;
  }

  return components.map((component) => rebaseMonitoringComponentToReference(component));
}

function repairMonitoringStatusV2TemplateSizing(components: Component[]): Component[] {
  const getReferenceDimension = (
    referenceComponent: TemplateComponentShape,
    dimension: "width" | "height",
  ): number | string | undefined => {
    const sizeValue = referenceComponent.size?.[dimension];
    if (typeof sizeValue === "string" || typeof sizeValue === "number") {
      return sizeValue;
    }

    const baseStyle =
      referenceComponent.style?.base &&
      typeof referenceComponent.style.base === "object" &&
      !Array.isArray(referenceComponent.style.base)
        ? referenceComponent.style.base
        : undefined;
    const styleValue = baseStyle?.[dimension];
    return typeof styleValue === "string" || typeof styleValue === "number"
      ? styleValue
      : undefined;
  };

  return components.map((component) => {
    const templateNodeId = component.properties?._templateNodeId;
    if (typeof templateNodeId !== "string") {
      return component;
    }

    const referenceComponent = MONITORING_V2_REFERENCE_BY_ID.get(templateNodeId);
    if (!referenceComponent || !component.size) {
      return component;
    }

    const nextSize = { ...component.size };
    let didUpdate = false;
    const referenceWidth = getReferenceDimension(referenceComponent, "width");
    const referenceHeight = getReferenceDimension(referenceComponent, "height");

    if (
      typeof referenceWidth === "string" &&
      nextSize.width !== referenceWidth
    ) {
      nextSize.width = referenceWidth;
      didUpdate = true;
    }

    if (
      typeof referenceHeight === "string" &&
      nextSize.height !== referenceHeight
    ) {
      nextSize.height = referenceHeight;
      didUpdate = true;
    }

    if (!didUpdate) {
      return component;
    }

    return {
      ...component,
      size: nextSize,
    };
  });
}

export function migrateLegacyTemplateComponents(
  components: Component[],
): Component[] {
  if (!Array.isArray(components) || components.length === 0) {
    return components;
  }

  let migratedComponents = components.map((component) => cloneComponent(component));

  migratedComponents = migrateLegacyBreadcrumb(migratedComponents);
  for (const migration of LEGACY_MONITORING_CARD_MIGRATIONS) {
    migratedComponents = migrateLegacyMonitoringCard(migratedComponents, migration);
  }

  migratedComponents = rebaseMonitoringStatusLayout(migratedComponents);
  migratedComponents = repairMonitoringStatusV2TemplateSizing(migratedComponents);
  migratedComponents = repairWellLocationSummaryGaugeCards(migratedComponents);
  return migratedComponents;
}
